/**
 * Récupération d'altitude pour les fichiers qui n'en portent pas.
 *
 * Les GPX d'organisateur sont souvent dépourvus d'altitude : ni profil, ni
 * dénivelé, ni marqueurs sur la courbe. Le relais `api/elevation.php` interroge
 * OpenTopoData puis, en secours, Open-Meteo — la cascade et la mise en cache
 * vivent côté serveur, partagées entre visiteurs.
 *
 * **Tout ou rien.** Un lot en échec fait échouer la récupération entière : un
 * profil dont une portion vient d'une source et le reste d'une autre — ou pire,
 * d'aucune — fausserait le dénivelé sans que rien ne le signale.
 *
 * Référence : `ElevationFetcher.kt`, `docs/altitude.md`.
 */

import { haversine } from './geo'
import type { TrackPoint } from './types'

/** Chemin relatif : l'application peut vivre dans un sous-répertoire. */
const ELEVATION_ENDPOINT = './api/elevation.php'

/** Espacement visé entre deux points interrogés, en mètres. */
export const SAMPLE_INTERVAL_M = 100

/**
 * Nombre maximal de points interrogés.
 *
 * Au-delà, l'espacement s'élargit : les modèles d'altitude ont une résolution
 * de 25 à 90 m, interroger plus finement ne rapporterait rien.
 */
export const MAX_SAMPLES = 5000

/** Points par requête, plafond imposé par le relais. */
const BATCH_SIZE = 100

/**
 * Rangs des points à interroger.
 *
 * Un point tous les 100 m environ, en gardant toujours le premier et le
 * dernier : les extrémités portent le départ et l'arrivée, que l'interpolation
 * ne saurait pas retrouver.
 */
export function sampleIndices(cumulative: readonly number[]): number[] {
  const last = cumulative.length - 1
  if (last < 0) return []

  const total = cumulative[last]!
  if (total <= 0) return [0]

  const interval = Math.max(SAMPLE_INTERVAL_M, total / (MAX_SAMPLES - 1))
  const indices = [0]
  let nextThreshold = interval

  for (let i = 1; i < last; i += 1) {
    if (cumulative[i]! >= nextThreshold) {
      indices.push(i)
      nextThreshold = cumulative[i]! + interval
    }
  }

  if (indices[indices.length - 1] !== last) indices.push(last)
  return indices
}

/**
 * Redistribue les altitudes échantillonnées sur tous les points.
 *
 * L'interpolation porte sur les **distances cumulées** et non sur les rangs :
 * deux points voisins peuvent être séparés d'un mètre ou de cent, et un
 * découpage par rang produirait des pentes fantaisistes.
 */
export function interpolateSamples(
  cumulative: readonly number[],
  sampleIdx: readonly number[],
  sampleEle: readonly number[],
): number[] {
  const result = new Array<number>(cumulative.length).fill(0)
  if (sampleIdx.length === 0) return result

  if (sampleIdx.length === 1) return result.fill(sampleEle[0] ?? 0)

  let s = 0

  for (let i = 0; i < cumulative.length; i += 1) {
    while (s < sampleIdx.length - 2 && i > sampleIdx[s + 1]!) s += 1

    const a = sampleIdx[s]!
    const b = sampleIdx[s + 1]!

    if (i <= a) {
      result[i] = sampleEle[s]!
    } else if (i >= b) {
      result[i] = sampleEle[s + 1]!
    } else {
      const denominator = cumulative[b]! - cumulative[a]!
      result[i] =
        denominator <= 0
          ? sampleEle[s]!
          : sampleEle[s]! +
            (sampleEle[s + 1]! - sampleEle[s]!) *
              ((cumulative[i]! - cumulative[a]!) / denominator)
    }
  }

  return result
}

/** Distances cumulées le long d'une suite de points. */
export function cumulativeDistances(points: readonly TrackPoint[]): number[] {
  const distances = new Array<number>(points.length).fill(0)

  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1]!
    const current = points[i]!
    distances[i] =
      distances[i - 1]! +
      haversine(previous.lat, previous.lon, current.lat, current.lon)
  }

  return distances
}

export interface ElevationProgress {
  /** Avancement de 0 à 1. */
  readonly ratio: number
}

export interface FetchElevationsOptions {
  readonly signal?: AbortSignal
  readonly onProgress?: (progress: ElevationProgress) => void
}

/**
 * Interroge le relais pour une suite de points.
 *
 * Les lots partent l'un après l'autre : le relais appelle des services publics
 * à quota, et paralléliser ne ferait que multiplier les refus.
 *
 * @returns Les altitudes, dans l'ordre des points reçus, ou `null` si un seul
 *   lot a échoué.
 * @throws Si la requête est annulée, pour distinguer l'abandon de l'échec.
 */
export async function fetchElevations(
  points: readonly { lat: number; lon: number }[],
  options: FetchElevationsOptions = {},
): Promise<number[] | null> {
  if (points.length === 0) return []

  const elevations: number[] = []

  for (let offset = 0; offset < points.length; offset += BATCH_SIZE) {
    const batch = points.slice(offset, offset + BATCH_SIZE)
    const coords = batch
      .map((point) => `${point.lat.toFixed(6)},${point.lon.toFixed(6)}`)
      .join(';')

    let payload: unknown
    try {
      // En `POST` : le serveur journalise l'URL avec l'adresse IP du visiteur,
      // et des coordonnées en paramètre de requête y resteraient pour toute la
      // durée de rétention de l'hébergeur. Le corps, lui, n'est pas journalisé.
      const response = await fetch(ELEVATION_ENDPOINT, {
        method: 'POST',
        // Explicite : c'est ce type que PHP sait décomposer en `$_POST`.
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ coords }),
        signal: options.signal,
      })
      if (!response.ok) return null
      payload = await response.json()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return null
    }

    const values = (payload as { elevations?: unknown } | null)?.elevations
    if (!Array.isArray(values) || values.length !== batch.length) return null
    if (!values.every((value) => typeof value === 'number')) return null

    elevations.push(...(values as number[]))

    options.onProgress?.({ ratio: elevations.length / points.length })
  }

  return elevations
}
